import * as yup from 'yup';
import { ArrowDown, ArrowUp, Edit, Settings, X } from 'react-feather';
import { DocumentNode } from 'graphql';
import { useEffect } from 'react';
import { useForm } from 'react-form-state-manager';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import sortBy from 'lodash/sortBy';

import {
  GetFieldsQuery as GetFields,
  ParticipantAttribute,
  ParticipantFieldScope,
  Permission,
} from '__generated__/graphql';

import { encodeUrl, getServerErrors } from '../../../../common/helpers';
import { useNotifier } from '../../../../common/Notifications/NotificationProvider';
import AddParticipantFieldForm from './AddParticipantFieldForm';
import EditParticipantAttributeMutation from './EditParticipantAttributeMutation';
import EditParticipantFieldForm from './EditParticipantFieldForm';
import GetFieldsQuery from './GetFieldsQuery';
import ProtectedSection from '../../../Common/ProtectedSection';
import SortParticipantFieldsMutation from './SortParticipantFieldsMutation';
import TicketSelectionForm from '../../../Common/TicketSelectionForm';
import ToggleMailPermissionMutation from './ToggleMailPermissionMutation';
import ToggleParticipantFieldMutation from './ToggleParticipantFieldMutation';
import UI from '../../../../common/UI';
import useMediaDevice from '../../../../common/useMediaDevice';
import useMutation from '../../../../api/useMutation';
import useProject from '../../../useProject';
import useQuery from '../../../../api/useQuery';
import useUrlState from '../../../../common/useUrlState';
import useValidation from '../../../../common/useValidation';

type Event = GetFields['project']['event'];
type ParticipantField = Event['participant_fields'][0];

interface UrlState {
  editing: string | null;
  adding: ParticipantFieldScope | null;
}

const parseUrl = (state: any) => ({
  ...state,
  adding: state.adding || null,
  editing: state.editing || null,
});

interface FieldsPageProps {
  eventId: string;
  refetchQueries?: DocumentNode[];
}

const FieldsPage = ({ eventId, refetchQueries = [GetFieldsQuery] }: FieldsPageProps) => {
  const { t } = useTranslation();
  const project = useProject();
  const history = useHistory();

  const { data } = useQuery(
    GetFieldsQuery,
    {
      variables: {
        project_id: project.id,
        event_id: eventId,
      },
    },
  );

  const event = data?.project.event;
  const fields = event?.participant_fields || [];
  const mailPermissions = (data?.project.mail_permissions || [])
    .filter((mailPermission) => !mailPermission.interest_list_event);

  const orderFields = sortBy(fields.filter(
    ({ scope }) => scope === ParticipantFieldScope.order,
  ), 'position');
  const purchaseFields = sortBy(fields.filter(
    ({ scope }) => scope === ParticipantFieldScope.purchase,
  ), 'position');

  const [urlState] = useUrlState<UrlState>({
    parse: parseUrl,
  });

  const closeRedirect = `?${encodeUrl({ ...urlState, adding: null, editing: null, type: null })}`;

  if (!event) {
    return <UI.PageLoader />;
  }

  return (
    <UI.Section>
      <UI.GridContainer>
        <UI.SectionHeading>
          {t('fields')}
        </UI.SectionHeading>

        <UI.Card sc={{ padding: [4, 6] }}>
          <UI.FormGrid sc={{ columns: ['1fr', '1fr 2fr'], gutter: 1.5 }}>
            <UI.Div>
              <UI.H4>{t('standard_fields')}</UI.H4>
              <UI.InputDescription>
                {t('participant_fields_description')}
              </UI.InputDescription>
            </UI.Div>
            <UI.FormGrid sc={{ columns: ['1fr', '4fr 1fr'] }}>
              <UI.Div>
                <UI.FormGrid sc={{ columns: '1fr', gutter: 0.5 }}>
                  <UI.FormGrid sc={{ columns: '1fr 1fr' }}>
                    <UI.Div sc={{ pt: 0.25 }}>
                      <UI.InputLabel sc={{ mb: 0 }}>
                        {t('first_and_last_name')}
                      </UI.InputLabel>
                    </UI.Div>

                    <UI.Div sc={{ textAlign: 'right' }}>
                      <UI.Toggle checked disabled />
                    </UI.Div>
                  </UI.FormGrid>

                  <UI.FormGrid sc={{ columns: '1fr 1fr' }}>
                    <UI.Div sc={{ pt: 0.25 }}>
                      <UI.InputLabel sc={{ mb: 0 }}>
                        {t('email_address')}
                      </UI.InputLabel>
                    </UI.Div>

                    <UI.Div sc={{ textAlign: 'right' }}>
                      <UI.Toggle checked disabled />
                    </UI.Div>
                  </UI.FormGrid>

                  {event.optional_participant_attributes.map((attribute) => (
                    <ParticipantAttributeToggle
                      attribute={attribute}
                      event={event}
                      editing={urlState.editing === attribute}
                      closeTo={closeRedirect}
                      key={attribute}
                      refetchQueries={refetchQueries}
                    />
                  ))}
                </UI.FormGrid>
              </UI.Div>
            </UI.FormGrid>
          </UI.FormGrid>

          <UI.HR />

          <UI.FormGrid sc={{ columns: ['1fr', '1fr 2fr'], gutter: 1.5 }}>
            <UI.Div>
              <UI.H4>{t('purchase_fields')}</UI.H4>
              <UI.InputDescription>
                {t('purchase_fields_description')}
              </UI.InputDescription>
            </UI.Div>
            <UI.FormGrid sc={{ columns: ['1fr', '4fr 1fr'] }}>
              <UI.Div>
                <UI.FormGrid sc={{ columns: '1fr', gutter: 0.5 }}>
                  {purchaseFields.map((field, index) => (
                    <ParticipantFieldToggle
                      event={event}
                      fields={purchaseFields}
                      index={index}
                      editing={urlState.editing === field.id}
                      closeTo={closeRedirect}
                      onDelete={() => history.push(closeRedirect)}
                      refetchQueries={refetchQueries}
                      key={field.id}
                    />
                  ))}

                  <UI.Div>
                    {urlState.adding !== ParticipantFieldScope.purchase && (
                      <UI.LinkButton
                        to={`?${encodeUrl({ ...urlState, adding: ParticipantFieldScope.purchase, editing: null })}`}
                        sc={{ brand: 'secondary' }}
                      >
                        {t('add_field')}
                      </UI.LinkButton>
                    )}

                    {urlState.adding === ParticipantFieldScope.purchase && (
                      <AddParticipantFieldForm
                        event={event}
                        scope={ParticipantFieldScope.purchase}
                        closeTo={closeRedirect}
                        onSuccess={() => history.push(closeRedirect)}
                        refetchQueries={refetchQueries}
                      />
                    )}
                  </UI.Div>
                </UI.FormGrid>
              </UI.Div>
            </UI.FormGrid>
          </UI.FormGrid>

          <UI.HR />

          <UI.FormGrid sc={{ columns: ['1fr', '1fr 2fr'], gutter: 1.5 }}>
            <UI.Div>
              <UI.H4>{t('order_fields')}</UI.H4>
              <UI.InputDescription>
                {t('order_fields_description')}
              </UI.InputDescription>
            </UI.Div>
            <UI.FormGrid sc={{ columns: ['1fr', '4fr 1fr'] }}>
              <UI.Div>
                <UI.FormGrid sc={{ columns: '1fr', gutter: 0.5 }}>
                  {orderFields.map((field, index) => (
                    <ParticipantFieldToggle
                      event={event}
                      fields={orderFields}
                      index={index}
                      editing={urlState.editing === field.id}
                      closeTo={closeRedirect}
                      onDelete={() => history.push(closeRedirect)}
                      refetchQueries={refetchQueries}
                      key={field.id}
                    />
                  ))}
                  <UI.Div>
                    {urlState.adding !== ParticipantFieldScope.order && (
                      <UI.LinkButton
                        to={`?${encodeUrl({ ...urlState, adding: ParticipantFieldScope.order, editing: null })}`}
                        sc={{ brand: 'secondary' }}
                      >
                        {t('add_field')}
                      </UI.LinkButton>
                    )}

                    {urlState.adding === ParticipantFieldScope.order && (
                      <AddParticipantFieldForm
                        event={event}
                        scope={ParticipantFieldScope.order}
                        closeTo={closeRedirect}
                        onSuccess={() => history.push(closeRedirect)}
                        refetchQueries={refetchQueries}
                      />
                    )}
                  </UI.Div>
                </UI.FormGrid>
              </UI.Div>
            </UI.FormGrid>
          </UI.FormGrid>

          <UI.HR />

          <UI.FormGrid sc={{ columns: ['1fr', '1fr 2fr'], gutter: 1.5 }}>
            <UI.Div>
              <UI.H4>{t('mail_opt_ins')}</UI.H4>
              <UI.InputDescription>
                {t('mail_opt_ins_description')}
              </UI.InputDescription>
            </UI.Div>
            <UI.FormGrid sc={{ columns: ['1fr', '4fr 1fr'] }}>
              <UI.Div>
                <UI.FormGrid sc={{ columns: '1fr', gutter: 0.5 }}>
                  {mailPermissions.map((mailPermission) => (
                    <MailPermissionToggle
                      mailPermission={mailPermission}
                      event={event}
                      key={mailPermission.id}
                    />
                  ))}

                  <ProtectedSection permissions={[Permission.messages]}>
                    <UI.Div>
                      <UI.LinkButton
                        to={`/${project.id}/communication/permissions`}
                        sc={{ brand: 'gray.200' }}
                      >
                        {t('manage_mail_permissions')}
                      </UI.LinkButton>
                    </UI.Div>
                  </ProtectedSection>
                </UI.FormGrid>
              </UI.Div>
            </UI.FormGrid>
          </UI.FormGrid>
        </UI.Card>
      </UI.GridContainer>
    </UI.Section>
  );
};

interface ParticipantAttributeToggleProps {
  attribute: ParticipantAttribute;
  event: Event;
  editing: boolean;
  closeTo?: string;
  refetchQueries?: DocumentNode[];
}

const ParticipantAttributeToggle = ({
  attribute, event, editing, closeTo, refetchQueries,
}: ParticipantAttributeToggleProps) => {
  const { t } = useTranslation();
  const project = useProject();
  const notifier = useNotifier();

  const schema = yup.object().shape({
    enabled: yup.boolean(),
    tickets: yup.array(yup.object({
      id: yup.string(),
    })),
  });

  const enabled = event.participant_attributes.indexOf(attribute) !== -1;

  const getEnabledTickets = (event: Event) => event.tickets
    .filter((ticket) => ticket.participant_attributes.indexOf(attribute) !== -1)
    .map(({ id }) => ({ id }));

  const hasEnabledTickets = getEnabledTickets(event).length > 0;

  const getValues = (event: Event) => ({
    tickets: getEnabledTickets(event),
  });

  const form = useForm({
    values: getValues(event),
  });

  const [editParticipantAttribute, { loading, error }] = useMutation(
    EditParticipantAttributeMutation,
    {
      variables: {
        input: {
          id: event.id,
          participant_attribute: attribute,
          enabled,
          tickets: form.values.tickets,
        },
      },
      onCompleted: ({ editParticipantAttribute }) => {
        notifier.success(t('notifications.field_saved'));
        form.reset(getValues(editParticipantAttribute));
      },
      refetchQueries,
    },
  );

  const [toggleParticipantAttribute] = useMutation(
    EditParticipantAttributeMutation,
    {
      variables: {
        input: {
          id: event.id,
          participant_attribute: attribute,
          enabled: !enabled,
        },
      },
      onCompleted: ({ editParticipantAttribute }) => {
        const enabled = editParticipantAttribute.participant_attributes.indexOf(attribute) !== -1;
        notifier.success(t(`notifications.field_${!enabled ? 'de' : ''}activated`));
      },
      refetchQueries,
    },
  );

  const { valid, errors } = useValidation({
    schema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  const [urlState] = useUrlState<UrlState>({
    parse: parseUrl,
  });

  useEffect(() => {
    if (!editing) {
      form.reset();
    }
  }, [editing]);

  return (
    <UI.Card
      sc={{
        outline: editing ? 'gray.200' : 'transparent',
        padding: editing ? 4 : 0,
        mb: 0,
        transitionPadding: true,
      }}
    >
      <UI.Form onSubmit={() => editParticipantAttribute()}>
        <UI.FormGrid sc={{ columns: ['2fr 5fr', '3fr 4fr'] }}>
          <UI.Div sc={{ muted: !enabled || !hasEnabledTickets, pt: 0.25 }}>
            <UI.InputLabel sc={{ mb: 0 }}>
              {t(attribute)}
              {' '}
              {attribute === 'gender' && (
                <ProtectedSection permissions={[Permission.admin]}>
                  <UI.Link to={`/${project.id}/project/features`} sc={{ secondary: true, color: 'gray.400' }}>
                    <UI.Icon>
                      <Settings />
                    </UI.Icon>
                  </UI.Link>
                </ProtectedSection>
              )}
            </UI.InputLabel>
          </UI.Div>

          <UI.Div sc={{ textAlign: 'right' }}>
            <UI.LinkButton
              to={`?${encodeUrl({ ...urlState, adding: null, editing: editing ? null : attribute })}`}
              sc={{ blank: true, size: 'sm', color: 'gray.400', mr: 1 }}
            >
              {editing && (
                <UI.Icon>
                  <X />
                </UI.Icon>
              )}
              {!editing && (
                <UI.Icon>
                  {t('edit')}
                </UI.Icon>
              )}
            </UI.LinkButton>
            {' '}
            <UI.Toggle checked={enabled} onChange={() => toggleParticipantAttribute()} />
          </UI.Div>
        </UI.FormGrid>

        {editing && (
          <>
            <UI.HR />

            <TicketSelectionForm
              form={form}
              tickets={event.tickets}
              errors={errors}
              event={event}
            />

            <UI.HR />

            <UI.FormButtons
              form={form}
              submitLabel={t('save_field')}
              closeTo={closeTo}
              loading={loading}
              valid={valid}
            />
          </>
        )}
      </UI.Form>
    </UI.Card>
  );
};

interface MailPermission {
  id: string;
  title: string;
  events: {
    id: string;
  }[];
}

interface MailPermissionToggleProps {
  mailPermission: MailPermission;
  event: Event;
}

const MailPermissionToggle = ({ mailPermission, event }: MailPermissionToggleProps) => {
  const { t } = useTranslation();
  const notifier = useNotifier();

  const enabled = mailPermission.events.filter(({ id }) => id === event.id).length === 1;

  const [toggleMailPermission] = useMutation(
    ToggleMailPermissionMutation,
    {
      variables: {
        input: {
          id: mailPermission.id,
          events: [event.id],
          enable: !enabled,
        },
      },
      optimisticResponse: ({ input }) => ({
        toggleMailPermission: {
          id: mailPermission.id,
          events: input.enable
            ? input.events.map((id) => ({
              id,
              mail_permissions: [{ id: mailPermission.id, __typename: 'MailPermission' }],
              __typename: 'Event' as const,
            })) : [],
          __typename: 'MailPermission' as const,
        },
      }),
      onCompleted: () => (
        notifier.success(t(`notifications.mail_permission_${!enabled ? 'de' : ''}activated`))
      ),
    },
  );

  return (
    <UI.FormGrid sc={{ columns: '1fr 1fr' }}>
      <UI.Div sc={{ muted: !enabled, pt: 0.25 }}>
        <UI.InputLabel sc={{ inlineBlock: true, mb: 0 }}>
          {mailPermission.title}
        </UI.InputLabel>
      </UI.Div>

      <UI.Div sc={{ textAlign: 'right' }}>
        <UI.Toggle
          checked={enabled}
          onChange={() => toggleMailPermission()}
        />
      </UI.Div>
    </UI.FormGrid>
  );
};

interface ParticipantFieldToggleProps {
  event: Event;
  fields: ParticipantField[];
  index: number;
  editing?: boolean;
  closeTo?: string;
  onDelete?: () => void;
  refetchQueries?: DocumentNode[];
}

const ParticipantFieldToggle = ({
  event, fields, index, editing = false, closeTo, onDelete, refetchQueries,
}: ParticipantFieldToggleProps) => {
  const { t } = useTranslation();
  const notifier = useNotifier();
  const device = useMediaDevice();

  const [urlState] = useUrlState<UrlState>({
    parse: parseUrl,
  });

  const field = fields[index];

  const [toggleParticipantField] = useMutation(
    ToggleParticipantFieldMutation,
    {
      variables: {
        input: {
          field: { id: field.id },
          enabled: !field.enabled,
        },
      },
      optimisticResponse: {
        toggleParticipantField: {
          id: field.id,
          enabled: !field.enabled,
          __typename: 'ParticipantField',
        },
      },
      onCompleted: () => (
        notifier.success(t(`notifications.field_${!field.enabled ? 'de' : ''}activated`))
      ),
      refetchQueries,
    },
  );

  const [sortParticipantFields] = (
    useMutation(
      SortParticipantFieldsMutation,
      {
        optimisticResponse: (variables) => ({
          sortParticipantFields: variables.input.fields.map((field, index) => ({
            id: field.id,
            position: index + 1,
            __typename: 'ParticipantField' as const,
          })),
        }),
        onCompleted: () => (
          notifier.success(t('notifications.field_moved'))
        ),
        refetchQueries,
        awaitRefetchQueries: true,
      },
    )
  );

  const moveUp = () => moveField(-1);
  const moveDown = () => moveField(1);
  const moveField = (distance: number) => {
    const fieldsData = fields.map(({ id }) => ({ id }));
    const nextField = fieldsData[index + distance];
    fieldsData[index + distance] = { id: field.id };
    fieldsData[index] = nextField;

    sortParticipantFields({
      variables: {
        input: {
          fields: fieldsData,
        },
      },
    });
  };

  return (
    <UI.Card
      sc={{
        outline: editing ? 'gray.200' : 'transparent',
        padding: editing ? 4 : 0,
        mb: 0,
        transitionPadding: true,
      }}
    >
      <UI.FormGrid sc={{ columns: ['2fr 5fr', '3fr 4fr'] }}>
        <UI.Div sc={{ muted: !field.enabled, pt: 0.25 }}>
          <UI.InputLabel sc={{ mb: 0 }}>
            {field.title}
          </UI.InputLabel>
        </UI.Div>
        <UI.Div sc={{ textAlign: 'right' }}>
          <UI.Button
            sc={{ blank: true, size: 'sm', color: 'gray.400' }}
            onClick={() => moveUp()}
            disabled={index === 0}
          >
            <UI.Icon>
              <ArrowUp />
            </UI.Icon>
          </UI.Button>
          {' '}
          <UI.Button
            sc={{
              blank: true,
              size: 'sm',
              color: 'gray.400',
              mr: 1,
            }}
            onClick={() => moveDown()}
            disabled={index === fields.length - 1}
          >
            <UI.Icon>
              <ArrowDown />
            </UI.Icon>
          </UI.Button>
          {' '}
          <UI.LinkButton
            to={`?${encodeUrl({ ...urlState, adding: null, editing: editing ? null : field.id })}`}
            sc={{
              blank: true,
              size: 'sm',
              color: 'gray.400',
              mr: 1,
            }}
          >
            {editing && (
              <UI.Icon>
                <X />
              </UI.Icon>
            )}
            {!editing && device.isSmall && (
              <UI.Icon>
                <Edit />
              </UI.Icon>
            )}
            {!editing && !device.isSmall && t('edit')}
          </UI.LinkButton>
          {' '}
          <UI.Toggle checked={field.enabled} onChange={() => toggleParticipantField()} />
        </UI.Div>
      </UI.FormGrid>

      {editing && (
        <>
          <UI.HR />
          <EditParticipantFieldForm
            event={event}
            field={field}
            closeTo={closeTo}
            onDelete={onDelete}
            refetchQueries={refetchQueries}
          />
        </>
      )}
    </UI.Card>
  );
};

export default FieldsPage;
